import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { getNotifications, markRead, markAllRead } from '../api';
import { formatDateTime } from '../utils';

const TYPE_COLORS = {
  late_clockin: 'badge-orange',
  long_session: 'badge-blue',
  idle: 'badge-red',
  risk_trigger: 'badge-red',
  custom: 'badge-gray',
};

const TYPE_ICONS = {
  late_clockin: '⏰',
  long_session: '⏱',
  idle: '💤',
  risk_trigger: '⚠️',
  custom: '📢',
};

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);

  const load = async () => {
    try { setNotifications(await getNotifications()); } catch {}
  };

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  // Recalculate position whenever dropdown opens
  useEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const dropW = 340;
    // Prefer aligning right edge of dropdown to right edge of button
    // but clamp so it never goes off the right or left of the viewport
    let left = r.right - dropW;
    if (left < 8) left = 8;
    if (left + dropW > window.innerWidth - 8) left = window.innerWidth - dropW - 8;
    setPos({ top: r.bottom + 8, left });
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (btnRef.current && !btnRef.current.closest('[data-notif-root]')?.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Browser push for new unread
  useEffect(() => {
    if (!('Notification' in window)) return;
    if (Notification.permission === 'default') Notification.requestPermission();
  }, []);

  useEffect(() => {
    const unread = notifications.filter(n => !n.read);
    if (unread.length > 0 && Notification.permission === 'granted') {
      const latest = unread[0];
      const key = `notif_shown_${latest.id}`;
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        new Notification(latest.title, { body: latest.message, icon: '/favicon.ico' });
      }
    }
  }, [notifications]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const handleMarkRead = async (id) => {
    await markRead(id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: 1 } : n));
  };

  const handleMarkAll = async () => {
    await markAllRead();
    setNotifications(prev => prev.map(n => ({ ...n, read: 1 })));
  };

  const dropdown = open ? ReactDOM.createPortal(
    <div
      data-notif-root
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        width: Math.min(340, window.innerWidth - 16),
        background: '#fff',
        borderRadius: 14,
        boxShadow: '0 12px 40px rgba(0,0,0,.18)',
        border: '1px solid #e2e8f0',
        zIndex: 99999,
        overflow: 'hidden',
        fontFamily: 'Inter, Segoe UI, sans-serif',
      }}
    >
      {/* Header */}
      <div style={{ padding: '14px 16px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>
          Notifications
          {unreadCount > 0 && (
            <span style={{ marginLeft: 8, background: '#ef4444', color: '#fff', borderRadius: 99, padding: '1px 7px', fontSize: '0.7rem', fontWeight: 700 }}>
              {unreadCount}
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAll}
            style={{ fontSize: '0.75rem', color: '#4f46e5', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: '4px 8px', borderRadius: 6, transition: 'background .15s' }}
            onMouseEnter={e => e.target.style.background = '#eef2ff'}
            onMouseLeave={e => e.target.style.background = 'none'}
          >
            Mark all read
          </button>
        )}
      </div>

      {/* List */}
      <div style={{ maxHeight: 400, overflowY: 'auto' }}>
        {notifications.length === 0 ? (
          <div style={{ padding: '32px 16px', textAlign: 'center', color: '#64748b', fontSize: '0.875rem' }}>
            No notifications
          </div>
        ) : (
          notifications.map(n => (
            <div
              key={n.id}
              onClick={() => !n.read && handleMarkRead(n.id)}
              style={{
                padding: '12px 16px',
                borderBottom: '1px solid #f8fafc',
                background: n.read ? '#fff' : '#f0f4ff',
                cursor: n.read ? 'default' : 'pointer',
                transition: 'background .15s',
              }}
            >
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div style={{ fontSize: '1.1rem', flexShrink: 0, marginTop: 1 }}>
                  {TYPE_ICONS[n.type] || '📢'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                    <span style={{ fontWeight: n.read ? 500 : 700, fontSize: '0.855rem', color: '#1e293b', wordBreak: 'break-word' }}>
                      {n.title}
                    </span>
                    {!n.read && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#4f46e5', flexShrink: 0, display: 'inline-block' }} />}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#64748b', lineHeight: 1.45, wordBreak: 'break-word', marginBottom: 4 }}>
                    {n.message}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                    {formatDateTime(n.created_at)}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div data-notif-root style={{ position: 'relative' }} ref={btnRef}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          position: 'relative', background: open ? 'rgba(255,255,255,.2)' : 'rgba(255,255,255,.1)',
          border: 'none', borderRadius: 8, padding: '6px 8px', cursor: 'pointer', color: '#a5b4fc',
          transition: 'background .15s',
        }}
      >
        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute', top: 1, right: 1, minWidth: 16, height: 16, borderRadius: 99,
            background: '#ef4444', color: '#fff', fontSize: '0.62rem', fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px',
          }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
      {dropdown}
    </div>
  );
}
